import { type FC, useEffect } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { _isNil, _isNotBlank } from '@/littledash';
import Lookup from '@/components/UI/Lookup';
import type { SelectFieldProps, SelectOption, SelectOptionValue } from '../VerticalStepForm.model';

/** A select holds a value once its option carries a non-blank string or any number. */
const hasSelection = (value?: SelectOption<SelectOptionValue> | null): boolean =>
  typeof value?.value === 'number' ? Number.isFinite(value.value) : _isNotBlank(value?.value);

/**
 * Async paginated select field. Wraps the Lookup component with RHF Controller.
 * The form value holds the full selected option ({ label, value }) so the
 * label survives step collapse/reopen; VerticalStepForm flattens select fields
 * to their primitive value before invoking onSubmit.
 */
const SelectField: FC<SelectFieldProps> = ({ config, onValueChange }) => {
  const { name, label, showLabel, placeholder, loadOptions, rules, defaultValue } = config;
  const { control, getValues, setValue } = useFormContext();

  // Seed a dynamically-injected select with its server-derived default the first time it mounts with
  // no value (e.g. a detected SEND domain). Keyed on the field name so re-seeding a different field
  // works; skipped once the field already holds a value so a user override is never clobbered.
  useEffect(() => {
    if (_isNil(defaultValue)) {
      return;
    }
    const current = getValues(name);
    if (_isNil(current) || _isNil((current as SelectOption<SelectOptionValue> | null)?.value)) {
      setValue(name, defaultValue, { shouldValidate: true });
    }
  }, [name, defaultValue, getValues, setValue]);

  return (
    // Constrain select width; the upload field fills the step, so the cap is per-field.
    <div style={{ maxWidth: 400 }}>
      {showLabel && (
        <label htmlFor={name} className="db mb1 f6 near-black basier-med">
          {label}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        rules={{
          ...rules,
          validate: rules?.required
            ? (value) => (hasSelection(value) ? true : typeof rules.required === 'string' ? rules.required : 'Required')
            : undefined,
        }}
        render={({ field: { onChange, value } }) => (
          <Lookup
            name={name}
            inputId={name}
            loadOptions={loadOptions}
            handleSelect={(data: SelectOption<SelectOptionValue> | null) => {
              onChange(data ?? null);
              onValueChange?.();
            }}
            value={value ?? null}
            placeholder={placeholder ?? 'Search...'}
            cacheOptions={false}
          />
        )}
      />
    </div>
  );
};

export default SelectField;
